/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import DataTable from "react-data-table-component";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import { ContentCard } from "components/ContentCard";
import AiccBreadCrumb from "components/common/AiccBreadCrumb";
import CenterLoading from "components/common/CenterLoading";
import PrimaryButton from "components/Form/Buttons/PrimaryButton";
import BasicTextInput from "components/Form/Inputs/common/BasicTextInput";
import ConfirmActivateModal from "components/Modals/ConfirmActivateModal";
import CustomModal from "components/Modals/CustomModal";
import RuleEngineActions from "../components/RuleEngineActions";
import rulesApi from "services/apis/rulesApi";
import ruleEngine from "services/apis/ruleEngine";
import {
  CRUD_RULE_ENGINE_TYPES,
  getPathPrefix,
  nameCapitalize,
  reorder,
} from "utils/coreUtils";
import ToastUtils from "utils/ToastUtils";
import "../styles/styles.scss";

const RuleEngineRuleList = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewStatus = searchParams.get("a");
  const [ruleOverviewList, setRuleOverviewList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  // const [versionOverview, setVersionOverview] = useState();
  const [ruleOverviewDropdown, setRuleOverviewDropdown] = useState([]);
  const [selectedRule, setSelectedRule] = useState();
  const [selectedRuleType, setSelectedRuleType] = useState(-1);
  const [rulesTypesData, setRulesTypesData] = useState([]);
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("");
  const [maxPriority, setMaxPriority] = useState("");
  const [showPriority, setShowPriority] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [prioritySame, setPrioritySame] = useState(false);
  const [selectedRow, setSelectedRow] = useState({ row: "", checked: "" });
  const [versionsDetails, setVersionsDetails] = useState();
  const [ruleQueryString, setRuleQueryString] = useState("");
  const [showQuery, setShowQuery] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [toggleAllStatus, setToggleAllStatus] = useState(false);

  const [t] = useTranslation();
  const streamObj = useSelector((state) => state?.datastream?.selected);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
        color: "#004e5c",
        fontWeight: 800,
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        padding: "0.75rem",
        verticalAlign: "middle",
      },
    },
  };

  const getAllRules = useCallback(async () => {
    // setIsLoading(true);
    try {
      const response = await rulesApi.getAllRulesByDSConfigVersionId({
        dsConfigVersionId: params?.versionId,
      });
      let prioritisedResponse = response?.data?.sort(function (a, b) {
        const aPriority = a.priority === -1 ? Number.MAX_VALUE : a.priority;
        const bPriority = b.priority === -1 ? Number.MAX_VALUE : b.priority;

        return aPriority - bPriority;
      });
      // if any of the prioritised rule is active, then toggleAllStatus will be true
      const isAnyActive = prioritisedResponse.some((item) => {
        return item.isActive;
      });

      // if all the prioritised rules are active, only then toggleAllStatus will be true
      // const isAllActive = prioritisedResponse.every((item) => {
      //   return item.isActive || item.priority === -1;
      // });
      setToggleAllStatus(isAnyActive);
      setRuleOverviewList(prioritisedResponse);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
    // setIsLoading(false);
  }, [params?.versionId]);

  const getAllGroupByName = useCallback(async () => {
    // setIsLoading(true);
    try {
      const response = await rulesApi.getGroupNameByGroup({
        dsConfigVersionId: params?.versionId,
      });
      setRuleOverviewDropdown(response.data);
    } catch (error) {
      console.error(error);
    }
    // setIsLoading(false);
  }, [params?.versionId]);

  // for get version info by id
  const getVersionsDetails = useCallback(async () => {
    try {
      const response = await ruleEngine.getDSConfigVersionById({
        dsConfigVersionId: params?.versionId,
      });
      setVersionsDetails(response?.data);
    } catch (error) {
      setIsLoading(false);
    }
  }, [params?.versionId]);

  const getAllRuleTypes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await rulesApi.getAllRulesType();
      var rulesTypesList = response?.data.sort(function (a, b) {
        return a.rulesOrder - b.rulesOrder;
      });
      // let takeObj = response?.data?.findIndex(
      //   (ruleTypes) => ruleTypes?.label.toLowerCase() === "manual"
      // );

      // if (takeObj > -1) {
      //   const obj = response?.data[takeObj];
      //   let tempRouting = {
      //     isActive: true,
      //     isDeleted: false,
      //     rulesOrder: obj.rulesOrder + 1,
      //     rulesTypeId: obj.rulesTypeId,
      //     label: obj.label + " " + "Routing",
      //   };
      //   rulesTypesList.splice(takeObj + 1, 0, tempRouting);
      // }
      setRulesTypesData(rulesTypesList);
      await getVersionsDetails();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, [getVersionsDetails]);

  async function getNextPriorityForRules() {
    // setIsLoading(true);
    try {
      const response = await rulesApi.getNextPriorityForRules({
        dsConfigVersionId: params?.versionId,
      });
      setPriority(response?.data?.nextPriority);
      setMaxPriority(response?.data?.nextPriority);
    } catch (error) {
      console.error(error);
    }
    // setIsLoading(false);
  }

  useEffect(() => {
    getAllRules();
    getAllRuleTypes();
    getAllGroupByName();
  }, [getAllGroupByName, getAllRuleTypes, getAllRules]);

  const columns = useMemo(
    () => [
      {
        name: t("versionOverview.columns.ruleName"),
        // selector: (row) => nameCapitalize(row.name),
        selector: (row) => {
          return (
            <OverlayTrigger
              placement="auto"
              overlay={(props) => (
                <Tooltip id="button-tooltip" {...props}>
                  {nameCapitalize(row.name)}
                </Tooltip>
              )}
            >
              <a
                className={[
                  "version-name-text",
                  viewStatus === "true" ? "" : "active",
                ].join(" ")}
                style={{
                  textDecoration:
                    viewStatus === "true" ? "" : "underline !important",
                }}
                onClick={() => {
                  if (viewStatus === "false") {
                    navigate(
                      getPathPrefix(
                        `selected-datastream/rule-engine/versions/${params?.versionId}/rule/${row.rulesId}?t=${CRUD_RULE_ENGINE_TYPES.EDIT}`
                      )
                    );
                  }
                }}
              >
                {nameCapitalize(row.name)}
              </a>
            </OverlayTrigger>
          );
        },
        sortable: true,
        minWidth: "130px",
        center: true,
      },
      {
        name: t("versionOverview.columns.priority"),
        selector: (row) =>
          row?.priority === null
            ? "N/A"
            : row?.priority === -1
              ? "N/A"
              : row?.priority,
        sortable: true,
        minWidth: "100px",
        center: true,
      },
      ...(rulesTypesData ?? []).map((item) => {
        return {
          name: item.label,
          selector: (row) => {
            return row.rulesTypeId === item.rulesTypeId
              ? t("CommonKeys.yes")
              : t("CommonKeys.no");
          },
          // sortable: true,
          minWidth: "170px",
          center: true,
        };
      }),
      {
        name: t("versionOverview.columns.editBy"),
        selector: (row) =>
          row?.updatedBy === null
            ? nameCapitalize(row.createdBy)
            : nameCapitalize(row.updatedBy),
        sortable: true,
        minWidth: "150px",
        center: true,
      },
      {
        name: t("versionOverview.columns.status"),
        cell: (row) => (
          <div className="switch mt-1">
            <input
              type="checkbox"
              id="status"
              name="status"
              checked={row.isActive}
              disabled={viewStatus === "true" || changingStatus}
              onChange={(e) => {
                if (changingStatus) {
                  return;
                }
                selectedRow.row = row;
                setSelectedRow({ ...selectedRow });
                if (e.target.checked === true && row.priority === -1) {
                  setShowPriority(true);
                  selectedRow.checked = e.target.checked;
                  getNextPriorityForRules();
                } else {
                  subStatusHandler(row, e.target.checked);
                  selectedRow.checked = e.target.checked;
                }
                setPriority("");
              }}
            />
          </div>
        ),
        minWidth: "70px",
        center: true,
      },
      {
        name: t("versionOverview.columns.actions"),
        center: true,
        minWidth: "220px",
        cell: (row) => (
          <RuleEngineActions
            classes="g-0"
            editPath={`selected-datastream/rule-engine/versions/${params?.versionId}/rule/${row.rulesId}?t=${CRUD_RULE_ENGINE_TYPES.EDIT}`}
            viewPath={`selected-datastream/rule-engine/versions/${params?.versionId}/rule/${row.rulesId}?t=${CRUD_RULE_ENGINE_TYPES.VIEW}`}
            deleteMessage={
              <>
                {t("versionOverview.delete")} <b>{row?.name}</b>{" "}
                {t("versionOverview.rule")}
              </>
            }
            deleteId={row.rulesId}
            deleteHandler={deleteHandler}
            canDelete={viewStatus !== "true"}
            canEdit={viewStatus !== "true"}
            additonalActions={[
              {
                action: (
                  <OverlayTrigger
                    placement="auto"
                    overlay={(props) => (
                      <Tooltip id="copy-tooltip" {...props}>
                        {t("versionsForm.tooltip.copy")}
                      </Tooltip>
                    )}
                  >
                    <span className="action-icon">
                      <button
                        disabled={viewStatus === "true"}
                        onClick={async () => {
                          setIsLoading(true);
                          try {
                            const {
                              messages,
                              createdAt,
                              updatedAt,
                              createdBy,
                              updatedBy,
                              whereClauses,
                              ...data
                            } = row;
                            const newClauses = [
                              ...whereClauses.map((clause) => {
                                const {
                                  updatedAt: clauseUpdatedAt,
                                  createdAt: clauseCreatedAt,
                                  ...clauses
                                } = clause;
                                return clauses;
                              }),
                            ];
                            await rulesApi.copyRule({
                              ...data,
                              whereClauses: newClauses,
                              messageList: [...messages],
                            });
                            await getAllRules();
                            setIsLoading(false);
                          } catch (error) {
                            console.error(error);
                            setIsLoading(false);
                          }
                        }}
                      >
                        <i className="fa-regular fa-copy"></i>
                      </button>
                    </span>
                  </OverlayTrigger>
                ),
                sequence: 2,
              },
              {
                action: (
                  <span className="action-icon" key="view-query-action">
                    <button
                      onClick={() => {
                        setRuleQueryString(row?.queryString);
                        setShowQuery(row);
                      }}
                    >
                      <i className="fa-solid fa-database"></i>
                    </button>
                  </span>
                ),
                sequence: 3,
              },
            ]}
          />
        ),
      },
    ],
    [rulesTypesData, ruleOverviewList, viewStatus, params, changingStatus]
  );

  const deleteHandler = async (id) => {
    try {
      const response = await rulesApi.deleteRulesById({
        rulesId: id,
      });
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
      }
      getAllRules();
    } catch (error) {
      /* empty */
    }
  };

  const subStatusHandler = async (row) => {
    setChangingStatus(true);

    try {
      const response = await rulesApi.statusChangeForRules({
        rulesId: row.rulesId,
      });
      if (response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
      } else {
        toast.success(t(`statusCodes.${response?.data?.code}`));
        await getAllRules();
      }
    } catch (error) {
      //  if (error.response.data.isDSConfigVersionActiveAlready) {
      //    setAlreadyActive(error.response.data.isDSConfigVersionActiveAlready);
      //  }
    }
    setChangingStatus(false);
  };

  const routeUserSelectOptions = useMemo(
    () => [
      {
        label: "Show All",
        value: "",
      },
      ...ruleOverviewDropdown.map((e, index) => ({
        label: nameCapitalize(e),
        value: index,
      })),
    ],
    [ruleOverviewDropdown]
  );
  const routeUserSelectedOptions = useMemo(
    () => [
      {
        label: " Show All",
        value: -1,
      },
      ...rulesTypesData.map((item) => ({
        label: nameCapitalize(item.label),
        value: item.rulesTypeId,
      })),
    ],
    [rulesTypesData]
  );

  const filteredRuleItems = ruleOverviewList.filter(
    (item) =>
      (search?.trim()
        ? item.name.toLowerCase().includes(search.toLowerCase())
        : true) &&
      (selectedRule
        ? item.groupName.toLowerCase().includes(selectedRule?.toLowerCase())
        : true) &&
      (selectedRuleType === -1 ? true : item.rulesTypeId === selectedRuleType)
  );

  const onSavePriorityModal = async (isOverrite) => {
    const createRequest = {
      dsConfigVersionId: params?.versionId,
      isDeleted: false,
      name: selectedRow?.row?.name,
      isActive: true,
      routingUserId: selectedRow?.row?.routingUserId
        ? selectedRow?.row?.routingUserId
        : 0,
      rulesId: selectedRow?.row?.rulesId,
      groupName: selectedRow?.row?.groupName,
      priority: priority,
      rulesTypeId: selectedRow?.row?.ruleTypeSelectId,
    };

    setChangingStatus(true);

    // for a change as we are facing issue while updating priority, we are calling below api to reset all priority at once.
    if (typeof isOverrite === "boolean" && isOverrite) {
      createRequest["isOverrite"] = isOverrite;

      // find the index for source whose priority will be set
      const sourceIndex = filteredRuleItems.findIndex(
        (e) => e.rulesId === selectedRow?.row?.rulesId
      );

      if (sourceIndex > -1) {
        // set destination to the priority - 1
        const destinationIndex = parseInt(priority) - 1;

        // update it in filtered rule items so we can update and avoid removing it from the array that will be sent on server.
        filteredRuleItems[sourceIndex].priority = parseInt(priority);
        filteredRuleItems[sourceIndex].isActive = true;

        // reorder list
        const items = reorder(filteredRuleItems, sourceIndex, destinationIndex);

        // remove elements which has -1 priority
        const filteredByNotMinus1 = items.filter((e) => e.priority != -1);

        try {
          // and save by calling api.
          await rulesApi.updatePriorityForRules(
            filteredByNotMinus1.map((e, index) => ({
              rulesId: e.rulesId,
              priority: index + 1,
              isActive: true,
            }))
          );

          /* if (response.data.status == 200) {
            toast.success(t(`statusCodes.${response.data.code}`));
          } else if (response.data.code) {
            ToastUtils.serverError(t(`statusCodes.${response.data.code}`));
          } */
          setShowPriority(false);
        } catch (error) {
          console.log(error);
        }
      }
    }

    // even if priority has been changed, we need to change status to true so for that reason we are calling this api even if priority has been changed.

    try {
      const response = await rulesApi.saveOrUpdateRules(createRequest);
      if (response.status === 200) {
        // subStatusHandler(selectedRow.row, selectedRow.checked);
        if (response.data.status != 200) {
          ToastUtils.serverError(t(`statusCodes.${response?.data?.code}`));
        } else {
          toast.success(t(`statusCodes.${response?.data?.code}`));
        }
        setShowPriority(false);
        await getAllRules();
      }
    } catch (error) {
      if (error.response.data.isDSConfigVersionPriorityAlready) {
        setPrioritySame(error.response.data.isDSConfigVersionPriorityAlready);
      } else if (error.response.data.status != 200) {
        ToastUtils.serverError(t(`statusCodes.${error.response.data.code}`));
      }
      // toast.error(error.response?.data);
    }

    setChangingStatus(false);
  };

  //for error handling
  const updateError = (key, value) => {
    setErrors((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const updateErrorForInputs = (
    inputValue,
    minValue,
    inputKey,
    error,
    min,
    max
  ) => {
    let msg = "";

    if (!inputValue) {
      updateError(inputKey, error);
      setSubmitDisabled(true);
    } else if (min && parseInt(inputValue) < parseInt(min)) {
      // msg = `${inputKey} must be at least ${min} characters long.`;
      // msg = `Priority should be greater than 0 and less then or equal to ${max}`;
      msg = t("versionsForm.errorMessage.priorityRangeError", {
        max,
      });
      updateError(inputKey, msg);
      setSubmitDisabled(true);
    } else if (max && parseInt(inputValue) > parseInt(max)) {
      // msg = `${inputKey} is more than ${max} characters long.`;
      // msg = `Priority should be greater than 0 and less then or equal to ${max}`;
      msg = t("versionsForm.errorMessage.priorityRangeError", {
        max,
      });
      updateError(inputKey, msg);
      setSubmitDisabled(true);
    } else {
      updateError(inputKey, undefined);
      setSubmitDisabled(false);
    }
  };

  return (
    <div className="list-rule-engin-overview">
      <CustomModal
        size={"xl"}
        show={showQuery}
        handleClose={() => setShowQuery(false)}
        body={ruleQueryString}
        centered
        okTitle={t("CommonKeys.okButtonTitle")}
        onOk={() => setShowQuery(false)}
      />
      <CustomModal
        show={showPriority}
        centered
        noHeader
        body={
          <div className="col-lg-12 col-md-12">
            <BasicTextInput
              type="number"
              label={t("createRuleEngine.settingsTab.priority.label")}
              isRequired={true}
              groupProps={{
                controlId: "priority",
                // className: "mb-3",
              }}
              inputProps={{
                placeholder: t(
                  "createRuleEngine.settingsTab.priority.placeholder"
                ),
                min: 1,
                max: maxPriority,
              }}
              name="priority"
              value={priority}
              onChange={(e) => {
                updateErrorForInputs(
                  e.target.value,
                  undefined,
                  "priority",
                  "Please enter a priority",
                  e.target.min,
                  e.target.max
                );
                setPriority(e.target.value);
              }}
              formLabelClass={{
                className: "mt-3",
              }}
              onBlur={(e) => {
                updateErrorForInputs(
                  e.target.value,
                  undefined,
                  "priority",
                  "Please enter a priority",
                  e.target.min,
                  e.target.max
                );
              }}
            />
            {errors.priority && (
              <span
                style={{ height: "20px", fontSize: "13px" }}
                className="text-danger mt-1"
              >
                {errors.priority}
              </span>
            )}
          </div>
        }
        okTitle={t("CommonKeys.saveButtonTitle")}
        cancelTitle={t("CommonKeys.cancelButtonTitle")}
        centerFooter
        submitDisabled={submitDisabled}
        onOk={() => {
          onSavePriorityModal();
        }}
        onCancel={() => {
          setShowPriority(false);
        }}
      />
      <ConfirmActivateModal
        show={prioritySame}
        onOk={() => {
          onSavePriorityModal(true);
          setPrioritySame(false);
        }}
        onClose={() => {
          setPrioritySame(false);
        }}
        messageTitles={t("createRuleEngine.modalErrorMessage")}
      />
      <AiccBreadCrumb
        homeLink={getPathPrefix("dashboard")}
        title={streamObj?.customDataStreamName}
        items={t("ruleEngineOverview.breadCrumb.items", {
          returnObjects: true,
        })}
      />
      <ContentCard
        headerImgSrc="/assets/images/engine.svg"
        title={`${t("versionOverview.ruleOverview")} - ${streamObj?.customDataStreamName} - (${versionsDetails?.versionName})`}
        bodyContent={
          <>
            <div className="form">
              <div className="row">
                <div className="col-lg-4 col-md-4">
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t("versionOverview.searchHint")}
                      value={search}
                      style={{ fontSize: "1rem" }}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        if (e.target.value === "") {
                          setResetPaginationToggle(!resetPaginationToggle);
                        }
                      }}
                    />
                  </div>
                </div>
                <div
                  className="col-lg-4 col-md-4"
                  style={{
                    position: "relative",
                    zIndex: 3,
                  }}
                >
                  {/* <label className="mb-2 form-label">
                            {t("versionOverview.selectRule")}
                          </label> */}
                  <ReactSelect
                    classNamePrefix="select"
                    value={routeUserSelectOptions.find(
                      (e) => e.label === selectedRule
                    )}
                    options={routeUserSelectOptions}
                    isOptionSelected={(option) => {
                      return option.label === selectedRule;
                    }}
                    onChange={(e) => {
                      if (e.value === "" && e.label === "Show All") {
                        getAllRules();
                        setSelectedRule(undefined);
                      } else {
                        setSelectedRule(e.label);
                      }
                    }}
                    placeholder={t("versionOverview.selectGroup")}
                  />
                </div>
                <div
                  className="col-lg-4 col-md-4"
                  style={{
                    position: "relative",
                    zIndex: 3,
                  }}
                >
                  {/* <label className="mb-2 form-label">
                            {t("versionOverview.selectRule")}
                          </label> */}
                  <ReactSelect
                    classNamePrefix="select"
                    value={routeUserSelectedOptions.find(
                      (e) => e.label === selectedRule
                    )}
                    options={routeUserSelectedOptions}
                    isOptionSelected={(option) => {
                      return option.label === selectedRule;
                    }}
                    onChange={(e) => {
                      setSelectedRuleType(e.value);
                    }}
                    placeholder={t("versionOverview.selectRuleType")}
                  />
                  <div className="switch my-3 d-flex gap-2 toggle-status-master">
                    <input
                      type="checkbox"
                      id="toggleAllRuleStatus"
                      name="toggleAllRuleStatus"
                      checked={toggleAllStatus}
                      aria-describedby="master-toggle-tooltip"
                      disabled={viewStatus === "true" || changingStatus}
                      onChange={async (e) => {
                        setIsLoading(true);
                        setToggleAllStatus(e.target.checked);
                        try {
                          await rulesApi.setAllRulesStatus(
                            params?.versionId,
                            e.target.checked
                          );
                          await getAllRules();
                          setIsLoading(false);
                        } catch (error) {
                          console.error(error);
                          setToggleAllStatus(!e.target.checked);
                          setIsLoading(false);
                        }
                      }}
                    />
                    <label htmlFor="toggleAllRuleStatus" className="fw-bold">
                      <span>{t("versionOverview.toggleStatusLabel")}</span>
                    </label>
                    <OverlayTrigger
                      placement="top"
                      overlay={(props) => (
                        <Tooltip id="master-toggle-tooltip" {...props}>
                          {t("versionOverview.toggleStatusDescription")}
                        </Tooltip>
                      )}
                    >
                      <i className="fa fa-info-circle"></i>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </div>

            <div className="data-table">
              <DataTable
                responsive
                persistTableHead
                keyField={"id"}
                progressPending={isLoading}
                progressComponent={<CenterLoading minHeight={"150px"} />}
                columns={columns}
                data={
                  filteredRuleItems
                  /* selectedRule
                            ? filteredRuleByGroupItems
                            : filteredRuleItems */
                  // ? fiteredRulesByData
                  // : filteredRuleItems
                }
                paginationResetDefaultPage={resetPaginationToggle}
                pagination
                customStyles={customStyles}
              />
            </div>
            <div className="text-end mt-3">
              <div className="d-block d-md-flex justify-content-end">
                <PrimaryButton
                  title={t("CommonKeys.backButtonTitle")}
                  icon="fa-solid fa-arrow-left"
                  onClick={() => {
                    navigate(
                      getPathPrefix("selected-datastream/rule-engine/versions")
                    );
                  }}
                  className="primaryButton"
                />
                <PrimaryButton
                  title={t("versionOverview.alterPriority")}
                  // title={"Alter Priority"}
                  onClick={() => {
                    navigate(
                      getPathPrefix(
                        `selected-datastream/rule-engine/versions/${params?.versionId}/rule/priority-selectable`
                      )
                    );
                  }}
                  className="primaryButton"
                  disabled={viewStatus === "true" ? true : false}
                />
                {/* <PrimaryButton
                          title={"Rule Builder"}
                          onClick={() => {
                            navigate(
                              getPathPrefix(
                                `selected-datastream/rule-engine/versions/${params?.versionId}/rule/builder`
                              )
                            );
                          }}
                          className="primaryButton"
                        /> */}
                <PrimaryButton
                  title={t("versionOverview.addRule")}
                  icon="fa-solid fa-plus"
                  onClick={() => {
                    navigate(
                      getPathPrefix(
                        `selected-datastream/rule-engine/versions/${params?.versionId}/rule/add?t=${CRUD_RULE_ENGINE_TYPES.CREATE}`
                      )
                    );
                  }}
                  className="primaryButton"
                  disabled={viewStatus === "true" ? true : false}
                />
              </div>
            </div>
          </>
        }
      />
    </div>
  );
};

export default RuleEngineRuleList;
